import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChatBot, ChatBotStore } from '@roc/feature-chat-bot';
import { Box, Typography, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useUserStore } from '@roc/feature-app-core';
import { ExpandLess } from '@material-ui/icons';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useRocInternalPortalStore } from '../../../hooks';
import { use } from 'echarts';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100vh',
  },
  rootEmbedded: {
    width: '100%',
    height: '100%',
    backgroundColor: '#f0f2f5',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
  },
  chatContainer: {
    width: '100%',
    height: '100%',
  },
  chatContainerEmbedded: {
    width: '100%',
    height: '100%',
    margin: 0,
  },
  header: {
    background: theme.palette.background.paper,
    padding: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  minimizedContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    height: '50px',
    padding: theme.spacing(0, 2),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  }
}));

export const ChatBotPage: React.FC = observer(() => {
  const classes = useStyles();
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const isEmbedded = urlParams.get('embedded') === 'true';
  const isFullScreen = urlParams.get('fullscreen') === 'true';
  const { userStore } = useUserStore();
  const { chatBotStore } = useRocInternalPortalStore();
  const inactivityTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeSessionTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isEmbedded && isFullScreen) {
      const sessionId = localStorage.getItem('chatBotSessionId');
      if (sessionId) {
        chatBotStore.config.sessionId = sessionId;
      }
    }
  }, [isEmbedded, isFullScreen, chatBotStore]);

  //Handle inactivity
  useEffect(() => {
    const resetTimeout = () => {
      if (inactivityTimeoutId.current) {
        clearTimeout(inactivityTimeoutId.current);
      }
      if (closeSessionTimeoutId.current) {
        clearTimeout(closeSessionTimeoutId.current);
      }

      inactivityTimeoutId.current = setTimeout(() => {
        if (chatBotStore.minimized) {
          chatBotStore.handleMaximize();
        }
        chatBotStore.sendInactivityMessage();
        closeSessionTimeoutId.current = setTimeout(() => {
          if (isEmbedded && !isFullScreen) {
            //this means the chat is open from nportal FE button
            chatBotStore.closeChatBotInParent();
            return;
          }
          localStorage.removeItem('chatBotSessionId');
          chatBotStore.startNewChat();
        }, 300000);
      }, 300000);
    };

    if (!chatBotStore.isInactivityMessageSent()) {
      resetTimeout();
    }

    return () => {
      if (inactivityTimeoutId.current) {
        clearTimeout(inactivityTimeoutId.current);
      }
      if (closeSessionTimeoutId.current) {
        clearTimeout(closeSessionTimeoutId.current);
      }
    };
  }, [chatBotStore.messages.length]);

  return (
    chatBotStore.minimized ? (
      <Box className={classes.minimizedContainer} onClick={chatBotStore.handleMaximize}>
        <Typography variant="h6">Roc Assistant</Typography>
        <ExpandLess />
      </Box>
    ) : (
      <Box className={classes.root}>
        <Paper elevation={0} className={classes.chatContainer}>
          {userStore.userInfoCheck && (
            <ChatBot
              isEmbedded={isEmbedded}
              isFullScreen={isFullScreen}
              store={chatBotStore}
            />
          )}
        </Paper>
      </Box>
    )
  );
});
